/**
 * KioskPage - Self-Service Kiosk Mode (Refactored)
 * 
 * Fullscreen ordering interface for customer self-service
 * Features: 
 * - SECURITY: Auto-fullscreen, keyboard blocking, secret admin exit
 * - Loyalty program welcome splash screen
 * - Member lookup by phone/email
 * - New member signup
 * - Category selection with big buttons
 * - Full menu customization
 * - Card-only payment with card input
 * - Order confirmation with queue number
 * - Upsell/Cross-sell popups
 * - Idle screen carousel
 * - Coupon code entry
 * - Enhanced accessibility mode
 * 
 * ADMIN EXIT: Long-press (5 sec) top-left corner → Enter PIN: 147258
 * 
 * This is the main entry point that wraps all kiosk views
 * with the shared KioskProvider context and security wrapper.
 */
import {
  KioskProvider,
  useKiosk,
  KioskWelcome,
  KioskMemberLookup,
  KioskMemberSignup,
  KioskConfirmation,
  KioskCheckout,
  KioskCart,
  KioskCategorySelect,
  KioskOrdering,
  KioskUpsellPopup,
  KioskIdleCarousel,
  KioskAccessibilityToggle
} from '@/components/kiosk';
import KioskSecurityWrapper from '@/components/kiosk/KioskSecurityWrapper';
import ItemCustomizationModal from '@/components/ordering/ItemCustomizationModal';

// Inner component that renders the appropriate view
function KioskView() {
  const { 
    view, 
    loading, 
    cart, 
    showUpsell, 
    closeUpsell, 
    handleAddUpsellItem,
    showIdleCarousel,
    dismissIdleCarousel,
    isAccessible,
    accessibilityEnabled,
    setAccessibilityEnabled,
    // Item Customization Modal state (moved to this level for proper overlay positioning)
    selectedItem,
    showCustomization,
    setShowCustomization,
    handleAddCustomizedItem,
    availableAddOns,
    settings,
    customer,
    coffeeClubStatus,
    fetchCoffeeClubStatus
  } = useKiosk();
  
  // Check if cart already has a coffee pass redemption
  const hasRedemptionInCart = cart?.some(item => item.isCoffeeClubRedemption || item.isCoffeePassRedemption);
  
  // Build coffee pass status for the modal
  const coffeePassForModal = coffeeClubStatus?.can_redeem_now && !hasRedemptionInCart 
    ? coffeeClubStatus 
    : null;

  // Callback when pass is redeemed
  const handleCoffeePassRedeemed = (data) => {
    if (customer?.email || customer?.phone) {
      fetchCoffeeClubStatus(customer.email, customer.phone);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Render view content
  const renderView = () => {
    switch (view) {
      case 'welcome':
        return <KioskWelcome />;
      case 'member-lookup':
        return <KioskMemberLookup />;
      case 'member-signup':
        return <KioskMemberSignup />;
      case 'confirmation':
        return <KioskConfirmation />;
      case 'checkout':
        return <KioskCheckout />;
      case 'cart':
        return <KioskCart />;
      case 'ordering':
        return <KioskCategorySelect />;  // Category selection with big buttons
      case 'ordering-items':
        return <KioskOrdering />;  // Items view after category selected
      default:
        return <KioskCategorySelect />;
    }
  };

  return (
    <>
      {renderView()}
      
      {/* Item Customization Modal - rendered at KioskView level for proper overlay positioning */}
      <ItemCustomizationModal
        item={selectedItem}
        open={showCustomization}
        onOpenChange={setShowCustomization}
        onAddToCart={handleAddCustomizedItem}
        availableAddOns={availableAddOns}
        theme="dark"
        showPrices={settings.show_prices}
        isAccessible={isAccessible}
        kioskPortraitMode={true}
        coffeePassStatus={coffeePassForModal}
        onCoffeePassRedeemed={handleCoffeePassRedeemed}
      />
      
      {/* Upsell Popup - shows after adding item to cart */}
      <KioskUpsellPopup
        show={showUpsell}
        onClose={closeUpsell}
        cartItems={cart}
        onAddItem={handleAddUpsellItem}
        isAccessible={isAccessible}
      />
      
      {/* Idle Carousel - shows when kiosk is inactive on welcome screen */}
      <KioskIdleCarousel
        show={showIdleCarousel}
        onDismiss={dismissIdleCarousel}
      />

      {/* Accessibility Toggle - always visible */}
      {view !== 'confirmation' && !showIdleCarousel && (
        <KioskAccessibilityToggle
          accessibilityEnabled={accessibilityEnabled}
          setAccessibilityEnabled={setAccessibilityEnabled}
          isAccessible={isAccessible}
        />
      )}
    </>
  );
}

// Main component with provider wrapper and security
export default function KioskPage() {
  return (
    <KioskProvider>
      <KioskSecurityWrapper>
        <KioskView />
      </KioskSecurityWrapper>
    </KioskProvider>
  );
}
